import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Stage, StageEvents, LocalStageStream, SubscribeType } from 'amazon-ivs-web-broadcast';
import streamApi from '../../services/streamApi';
import { readStoredUser } from '../../utils/navbarUser';
import { refreshSpadesBalance } from '../../utils/spadesBalance';

const MIN_DURATION = 30;
const COST_PER_30S = 500;

function fmtDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function makeViewerStrategy(getLocalStreams) {
  return {
    stageStreamsToPublish: () => getLocalStreams(),
    shouldPublishParticipant: () => true,
    shouldSubscribeToParticipant: () => SubscribeType.NONE,
  };
}

function fallbackCostForDuration(durationSeconds) {
  const normalizedDuration = Number(durationSeconds) > 0 ? Number(durationSeconds) : MIN_DURATION;
  const slots = Math.max(1, Math.ceil(normalizedDuration / MIN_DURATION));
  return slots * COST_PER_30S;
}

function normalizeNonNegativeNumber(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized >= 0 ? normalized : null;
}

export default function VideoRequestButton({ streamerId, streamerUsername, onLocalStreamChange }) {
  const [status, setStatus] = useState('idle'); // idle | confirming | requesting | pending | approved | rejected | ended
  const [error, setError] = useState('');
  const [localStream, setLocalStream] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [queuePosition, setQueuePosition] = useState(null);
  const [requestDurationSeconds, setRequestDurationSeconds] = useState(MIN_DURATION);
  const [configuredDurationSeconds, setConfiguredDurationSeconds] = useState(null);
  const [configuredCostSpades, setConfiguredCostSpades] = useState(null);
  const [requestCostSpades, setRequestCostSpades] = useState(null);
  const [requestsEnabled, setRequestsEnabled] = useState(false);
  const [viewerSlotsOpen, setViewerSlotsOpen] = useState(false);

  const wsRef = useRef(null);
  const stageRef = useRef(null);
  const localStageStreamsRef = useRef([]);
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const requestIdRef = useRef(null);
  const ivsTokenRef = useRef(null);
  const countdownRef = useRef(null);

  const currentUser = readStoredUser();
  const viewerId = currentUser?.id || '';
  const spadesBalance = currentUser?.spades ?? null;
  const displayedDurationSeconds = requestCostSpades === null
    ? (configuredDurationSeconds ?? requestDurationSeconds)
    : requestDurationSeconds;
  const displayedCostSpades = requestCostSpades
    ?? configuredCostSpades
    ?? fallbackCostForDuration(displayedDurationSeconds);
  const canAfford = spadesBalance === null || spadesBalance >= displayedCostSpades;

  useEffect(() => { localStreamRef.current = localStream; }, [localStream]);
  useEffect(() => {
    if (localVideoRef.current && localStream) localVideoRef.current.srcObject = localStream;
  }, [localStream]);

  useEffect(() => {
    if (!streamerId) return undefined;
    let cancelled = false;

    const loadConfiguredValues = async () => {
      try {
        const configRes = await streamApi.getVideoRequestConfig(streamerId);
        if (cancelled) return;

        setRequestsEnabled(Boolean(configRes?.requests_enabled));
        setViewerSlotsOpen(Boolean(configRes?.viewer_slots_open));

        const nextConfiguredDuration = normalizeNonNegativeNumber(
          configRes?.live_video_settings?.duration_seconds ?? configRes?.min_duration_seconds,
        );
        const nextConfiguredCost = normalizeNonNegativeNumber(
          configRes?.live_video_settings?.price_spades ?? configRes?.spade_cost,
        );

        if (nextConfiguredDuration !== null) {
          setConfiguredDurationSeconds(nextConfiguredDuration);
        }
        if (nextConfiguredCost !== null) {
          setConfiguredCostSpades(nextConfiguredCost);
        }
      } catch {
        // Public profile does not always expose live-video settings in dev/prod.
      }
    };

    loadConfiguredValues();
    const intervalId = setInterval(loadConfiguredValues, 3000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [streamerId]);

  const stopCountdown = useCallback(() => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const startCountdown = useCallback((endsAt) => {
    stopCountdown();
    const end = new Date(endsAt).getTime();
    const tick = () => {
      const left = Math.max(0, Math.round((end - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) { stopCountdown(); }
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
  }, [stopCountdown]);

  const leaveStage = useCallback(() => {
    if (stageRef.current) { try { stageRef.current.leave(); } catch {} stageRef.current = null; }
    localStageStreamsRef.current = [];
  }, []);

  const cleanup = useCallback(() => {
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    leaveStage();
    stopCountdown();
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
    setSecondsLeft(null);
    setQueuePosition(null);
    onLocalStreamChange?.(null);
  }, [leaveStage, stopCountdown, onLocalStreamChange]);

  useEffect(() => () => cleanup(), [cleanup]);

  useEffect(() => {
    if (status === 'idle') {
      setError('');
    }
  }, [status]);

  const ensureLocalMedia = useCallback(async () => {
    if (localStreamRef.current) {
      return localStreamRef.current;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      return stream;
    } catch {
      setError('Camera and microphone access is required to reconnect your live session.');
      return null;
    }
  }, []);

  const joinStageAsPublisher = useCallback(async (token) => {
    const stream = await ensureLocalMedia();
    if (!stream || !token) return;
    try {
      const stageStreams = stream.getTracks().map((track) => new LocalStageStream(track));
      localStageStreamsRef.current = stageStreams;
      const strategy = makeViewerStrategy(() => localStageStreamsRef.current);
      const stage = new Stage(token, strategy);
      await stage.join();
      stageRef.current = stage;
      onLocalStreamChange?.(stream);
    } catch {
      setError('Failed to join live stage. Please try again.');
      cleanup();
      setStatus('idle');
    }
  }, [cleanup, ensureLocalMedia, onLocalStreamChange]);

  const openSignaling = useCallback(() => {
    if (wsRef.current) return;
    const wsUrl = streamApi.buildSignalWsUrl(streamerId, 'viewer', viewerId);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = async (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }

      if (msg.type === 'extended') {
        if (msg.ends_at) startCountdown(msg.ends_at);
        return;
      }

      if (msg.type === 'approved') {
        const token = msg.ivs_token || ivsTokenRef.current;
        if (msg.ivs_token) ivsTokenRef.current = msg.ivs_token;
        if (msg.request_id) requestIdRef.current = msg.request_id;
        if (msg.ends_at) startCountdown(msg.ends_at);
        setQueuePosition(null);
        setStatus('approved');
        void refreshSpadesBalance();
        await joinStageAsPublisher(token);
      } else if (msg.type === 'rejected') {
        setStatus('rejected');
        cleanup();
      } else if (msg.type === 'end') {
        setStatus('ended');
        cleanup();
      }
    };

    ws.onerror = () => setError('Connection failed — please try again.');
    ws.onclose = () => { wsRef.current = null; };
  }, [streamerId, viewerId, joinStageAsPublisher, cleanup, startCountdown]);

  useEffect(() => {
    if (!streamerId || !viewerId) return undefined;
    let cancelled = false;

    const restoreRequest = async () => {
      try {
        const res = await streamApi.getMyVideoRequestStatus(streamerId);
        if (cancelled || !res?.request) return;

        requestIdRef.current = res.request.id || null;
        setRequestDurationSeconds(Number(res.request.duration_seconds) || MIN_DURATION);
        setRequestCostSpades(normalizeNonNegativeNumber(res.request.spade_cost));
        setQueuePosition(res.request.queue_position ?? res.queue_position ?? null);

        if (res.status === 'pending') {
          setStatus('pending');
          openSignaling();
          return;
        }

        if (res.status === 'approved') {
          if (res.ivs_token) {
            ivsTokenRef.current = res.ivs_token;
          }
          if (res.request.ends_at) {
            startCountdown(res.request.ends_at);
          }
          setStatus('approved');
          void refreshSpadesBalance();
          openSignaling();
          await joinStageAsPublisher(res.ivs_token || ivsTokenRef.current);
        }
      } catch {
        // Leave default idle state.
      }
    };

    restoreRequest();
    return () => { cancelled = true; };
  }, [openSignaling, startCountdown, streamerId, viewerId]);

  // Fallback poll while pending
  useEffect(() => {
    if (status !== 'pending') return undefined;
    const id = setInterval(async () => {
      try {
        const res = await streamApi.getMyVideoRequestStatus(streamerId);
        if (res?.status === 'approved' && !stageRef.current) {
          const token = res.ivs_token || ivsTokenRef.current;
          if (res.ivs_token) ivsTokenRef.current = res.ivs_token;
          if (res.request?.id) requestIdRef.current = res.request.id;
          if (res.request?.duration_seconds) {
            setRequestDurationSeconds(Number(res.request.duration_seconds) || MIN_DURATION);
          }
          if (res.request) {
            setRequestCostSpades(normalizeNonNegativeNumber(res.request.spade_cost));
          }
          if (res.request?.ends_at) startCountdown(res.request.ends_at);
          setQueuePosition(null);
          setStatus('approved');
          void refreshSpadesBalance();
          await joinStageAsPublisher(token);
        } else if (res?.status === 'pending') {
          if (res.request?.id) requestIdRef.current = res.request.id;
          if (res.request?.duration_seconds) {
            setRequestDurationSeconds(Number(res.request.duration_seconds) || MIN_DURATION);
          }
          if (res.request) {
            setRequestCostSpades(normalizeNonNegativeNumber(res.request.spade_cost));
          }
          setQueuePosition(res.request?.queue_position ?? res.queue_position ?? null);
          setStatus('pending');
        } else if (res?.status === 'rejected') {
          setRequestCostSpades(null);
          setStatus('rejected');
          cleanup();
        } else if (res?.status === 'ended') {
          setRequestCostSpades(null);
          setStatus('ended');
          cleanup();
        }
      } catch {}
    }, 3000);
    return () => clearInterval(id);
  }, [status, streamerId, joinStageAsPublisher, cleanup, startCountdown]);

  // Auto-end when countdown hits 0
  useEffect(() => {
    if (secondsLeft === 0 && status === 'approved') {
      (async () => {
        try { await streamApi.endVideoRequest(requestIdRef.current); } catch {}
        setStatus('ended');
        cleanup();
      })();
    }
  }, [secondsLeft, status, cleanup]);

  const handleRequest = async () => {
    setError('');

    if (!requestsEnabled || !viewerSlotsOpen) {
      setError('Viewer slots are currently closed.');
      return;
    }

    setStatus('requesting');

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    } catch {
      setError('Camera permission denied.');
      setStatus('picking');
      return;
    }

    setLocalStream(stream);
    localStreamRef.current = stream;

    try {
      const req = await streamApi.sendVideoRequest(streamerId, displayedDurationSeconds);
      requestIdRef.current = req?.id || null;
      setRequestDurationSeconds(Number(req?.duration_seconds) || MIN_DURATION);
      setRequestCostSpades(normalizeNonNegativeNumber(req?.spade_cost));
      openSignaling();
      if (req?.status === 'approved') {
        if (req.ivs_token) ivsTokenRef.current = req.ivs_token;
        if (req.ends_at) startCountdown(req.ends_at);
        setQueuePosition(null);
        setStatus('approved');
        void refreshSpadesBalance();
        await joinStageAsPublisher(req.ivs_token || ivsTokenRef.current);
      } else {
        setQueuePosition(req?.queue_position ?? null);
        setStatus('pending');
      }
    } catch (err) {
      setError(err.message || 'Failed to send request.');
      stream.getTracks().forEach((t) => t.stop());
      setLocalStream(null);
      localStreamRef.current = null;
      setStatus('picking');
    }
  };

  const handleCancel = async () => {
    if (requestIdRef.current && (status === 'pending' || status === 'approved')) {
      try {
        await streamApi.endVideoRequest(requestIdRef.current);
      } catch {}
    }
    requestIdRef.current = null;
    setRequestCostSpades(null);
    setQueuePosition(null);
    setStatus('idle');
    setError('');
    cleanup();
  };

  if (status === 'idle') {
    return (
      <div className="vr-btn-wrap">
        <button
          type="button"
          className="vr-btn vr-btn--request"
          onClick={() => {
            setError('');
            setStatus('confirming');
          }}
          disabled={!requestsEnabled || !viewerSlotsOpen}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          {requestsEnabled && viewerSlotsOpen ? 'Join Live Video' : 'Viewer Slots Closed'}
        </button>
        {error && <p className="vr-btn__error">{error}</p>}
      </div>
    );
  }

  if (status === 'confirming') {
    return (
      <div className="vr-btn-wrap vr-picker">
        <p className="vr-picker__title">Show your camera on stream</p>
        <div className="vr-picker__metrics">
          <div className="vr-picker__metric">
            <span className="vr-picker__metric-label">Minimum time</span>
            <span className="vr-picker__metric-value">{displayedDurationSeconds} seconds</span>
          </div>
          <div className="vr-picker__metric">
            <span className="vr-picker__metric-label">Cost</span>
            <span className="vr-picker__metric-value vr-picker__metric-value--accent">
              {displayedCostSpades.toLocaleString()} spades
            </span>
          </div>
        </div>
        <p className="vr-picker__rate">
          Streamer may extend your time after you join.
        </p>
        <p className="vr-picker__notice">
          If a live slot is open, your camera joins automatically. If all slots are full, you enter the paid queue and auto-join when the next slot opens.
        </p>
        {!requestsEnabled || !viewerSlotsOpen ? (
          <p className="vr-btn__error">Viewer slots are currently closed for this stream.</p>
        ) : null}
        {spadesBalance !== null && (
          <p className={`vr-picker__balance${canAfford ? '' : ' vr-picker__balance--low'}`}>
            Your balance: {spadesBalance.toLocaleString()} spades
          </p>
        )}
        <div className="vr-picker__actions">
          <button type="button" className="vr-btn vr-btn--cancel" onClick={handleCancel}>Cancel</button>
          <button
            type="button"
            className="vr-btn vr-btn--request"
            onClick={handleRequest}
            disabled={!canAfford}
          >
            {canAfford ? `Request (${displayedCostSpades.toLocaleString()} spades)` : 'Not enough spades'}
          </button>
        </div>
        {error && <p className="vr-btn__error">{error}</p>}
      </div>
    );
  }

  if (status === 'requesting') {
    return <div className="vr-btn-wrap"><span className="vr-btn__status">Accessing camera…</span></div>;
  }

  if (status === 'pending') {
    return (
      <div className="vr-btn-wrap">
        <div className="vr-pip vr-pip--pending">
          <video ref={localVideoRef} className="vr-pip__video" autoPlay muted playsInline />
          <div className="vr-pip__overlay">
            <span className="vr-pip__label">
              {queuePosition ? `Queued for ${streamerUsername} · position #${queuePosition}` : `Waiting for the next open slot on ${streamerUsername}'s stream`}
            </span>
            <span className="vr-pip__sub">
              {fmtDuration(requestDurationSeconds)} · {displayedCostSpades.toLocaleString()} spades charged when your live turn begins
            </span>
          </div>
        </div>
        <button type="button" className="vr-btn vr-btn--cancel" onClick={handleCancel}>Cancel</button>
      </div>
    );
  }

  if (status === 'approved') {
    const handleStop = async () => {
      try { await streamApi.endVideoRequest(requestIdRef.current); } catch {}
      setStatus('idle');
      cleanup();
    };
    return (
      <div className="vr-btn-wrap">
        <div className="vr-pip vr-pip--live vr-pip--approved">
          <span className="vr-pip__live-badge">● YOU ARE LIVE</span>
          {secondsLeft !== null && (
            <span className={`vr-pip__countdown${secondsLeft <= 10 ? ' vr-pip__countdown--urgent' : ''}`}>
              {secondsLeft}s left
            </span>
          )}
          <p className="vr-btn__status vr-btn__status--live">
            Your camera is on {streamerUsername}'s stream
          </p>
        </div>
        <button type="button" className="vr-btn vr-btn--cancel" onClick={handleStop}>Stop Camera</button>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="vr-btn-wrap">
        <p className="vr-btn__status vr-btn__status--rejected">You were removed from the live-video queue.</p>
        <button type="button" className="vr-btn vr-btn--request" onClick={handleCancel}>Try Again</button>
      </div>
    );
  }

  if (status === 'ended') {
    return (
      <div className="vr-btn-wrap">
        <p className="vr-btn__status">Your live session ended.</p>
        <button type="button" className="vr-btn vr-btn--request" onClick={handleCancel}>Request Again</button>
      </div>
    );
  }

  return null;
}

VideoRequestButton.propTypes = {
  streamerId: PropTypes.string.isRequired,
  streamerUsername: PropTypes.string.isRequired,
  onLocalStreamChange: PropTypes.func,
};

VideoRequestButton.defaultProps = {
  onLocalStreamChange: null,
};
